import { forwardRef } from "react";
import { coverFor } from "@/lib/defaultEventImages";
import { resolveCategory } from "@/lib/categories";

function formatDate(iso) {
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, {
      weekday: "long",
      day: "numeric",
      month: "short",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch { return iso; }
}

/**
 * ShareCard — 1080×1920 branded story card.
 *
 * Snapshotted by html-to-image into a JPEG for download / native share.
 *
 * IMPORTANT: pass `coverDataUrl` (a `data:image/...` string) when possible.
 * html-to-image fails to snapshot cross-origin <img>s on Safari/iOS even
 * when crossOrigin="anonymous" is set — the canvas gets "tainted" and the
 * resulting JPEG shows the category-coloured fallback in place of the
 * cover. Pre-converting the cover to an inline data URL in the parent
 * (SharePromptModal.buildStoryCardFile) avoids the cross-origin path
 * entirely. Falls back to the raw URL when a data URL isn't available
 * (good enough on Android and desktop).
 */
const ShareCard = forwardRef(function ShareCard({ event, coverDataUrl }, ref) {
  const cat = resolveCategory(event?.category);
  const cover = coverDataUrl || coverFor(event);
  const date = event?.date_time ? formatDate(event.date_time) : "TBD";
  const city = event?.city || "TBD";
  const category = event?.category || "";
  const host = event?.host_username
    ? `@${event.host_username}`
    : event?.host_name || "anon";
  const title = event?.title || "Untitled";

  return (
    <div
      ref={ref}
      data-testid="share-card-canvas"
      style={{
        width: 1080,
        height: 1920,
        background: "#F4F0E1",
        color: "#0A0A0A",
        fontFamily: "Outfit, system-ui, sans-serif",
        position: "relative",
        overflow: "hidden",
        boxSizing: "border-box",
        padding: 56,
      }}
    >
      {/* Cover frame */}
      <div
        style={{
          position: "relative",
          width: "100%",
          height: 960,
          borderRadius: 56,
          border: "10px solid #0A0A0A",
          overflow: "hidden",
          background: cat?.color || "#0A0A0A",
          boxShadow: "16px 16px 0 0 #0A0A0A",
        }}
      >
        <img
          src={cover}
          alt=""
          crossOrigin="anonymous"
          style={{
            width: "100%",
            height: "100%",
            objectFit: "cover",
            display: "block",
          }}
        />
        {category && (
          <div
            style={{
              position: "absolute",
              top: 32,
              left: 32,
              background: "#F4F0E1",
              color: "#0A0A0A",
              border: "6px solid #0A0A0A",
              borderRadius: 999,
              padding: "14px 28px",
              fontSize: 30,
              fontWeight: 800,
              letterSpacing: "0.22em",
              textTransform: "uppercase",
              lineHeight: 1,
            }}
          >
            {category}
          </div>
        )}
        <div
          style={{
            position: "absolute",
            right: 32,
            top: 32,
            background: "#D6FF00",
            color: "#0A0A0A",
            border: "6px solid #0A0A0A",
            borderRadius: 999,
            padding: "14px 28px",
            fontSize: 30,
            fontWeight: 800,
            letterSpacing: "0.18em",
            textTransform: "uppercase",
            lineHeight: 1,
            boxShadow: "6px 6px 0 0 #0A0A0A",
          }}
        >
          You're invited
        </div>
      </div>

      {/* Title */}
      <h1
        style={{
          fontFamily: "Outfit, system-ui, sans-serif",
          fontSize: 104,
          fontWeight: 800,
          lineHeight: 1.0,
          letterSpacing: "-0.02em",
          margin: "56px 0 0 0",
          color: "#0A0A0A",
          wordBreak: "break-word",
        }}
      >
        {title}
      </h1>

      {/* Info rows */}
      <div
        style={{
          marginTop: 48,
          display: "flex",
          flexDirection: "column",
          gap: 18,
        }}
      >
        <Row label="When"  value={date} />
        <Row label="Where" value={city} />
        <Row label="Host"  value={host} />
      </div>

      {/* Footer: logo + CTA */}
      <div
        style={{
          position: "absolute",
          bottom: 56,
          left: 56,
          right: 56,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          borderTop: "5px solid #0A0A0A",
          paddingTop: 28,
        }}
      >
        <img
          src="/inonit-logo.png"
          alt="inonit"
          crossOrigin="anonymous"
          style={{ height: 72, width: "auto", display: "block" }}
        />
        <div
          style={{
            background: "#0A0A0A",
            color: "#F4F0E1",
            border: "5px solid #0A0A0A",
            borderRadius: 999,
            padding: "16px 32px",
            fontSize: 30,
            fontWeight: 800,
            letterSpacing: "0.2em",
            textTransform: "uppercase",
            lineHeight: 1,
          }}
        >
          Find it on inonit
        </div>
      </div>
    </div>
  );
});

function Row({ label, value }) {
  return (
    <div style={{ display: "flex", alignItems: "baseline", gap: 24 }}>
      <span
        style={{
          fontSize: 26,
          letterSpacing: "0.26em",
          textTransform: "uppercase",
          color: "rgba(10,10,10,0.55)",
          fontWeight: 800,
          width: 150,
          flexShrink: 0,
          lineHeight: 1,
        }}
      >
        {label}
      </span>
      <span
        style={{
          fontSize: 52,
          fontWeight: 700,
          color: "#0A0A0A",
          lineHeight: 1.05,
          flex: 1,
          minWidth: 0,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
      >
        {value}
      </span>
    </div>
  );
}

export default ShareCard;
